/**
 * Single source of truth for what fields the storefront searches on.
 *
 * Used by:
 *   • `components/shop/SmartSearch.jsx`  — the popover dropdown
 *   • `pages/shop/CategoryPage.jsx`      — the `?q=` results page (incl. /c/All?q=…)
 *
 * Before this module existed the field list was duplicated in both files
 * (and drifted — "Diwali" matched 4 items in the dropdown but ZERO on the
 * results page). Keep this list as the only place that defines a
 * searchable field. The backend `/api/shop/search` endpoint mirrors the
 * same field set on the server side for API parity (`backend/server.py`
 * → `shop_search`).
 *
 * Each entry has a `key` (the property name on the item document) and a
 * human-friendly `label` rendered as a "matched on …" chip in the
 * dropdown. Array-valued fields are joined with " " when building the
 * searchable haystack so a single token can hit any element.
 */

export const SEARCH_FIELDS = [
    // Identity / taxonomy
    { key: "name",             label: "name" },
    { key: "category",         label: "category" },
    { key: "department_slug",  label: "department" },

    // Fabric — legacy single + multi-select array
    { key: "fabric",           label: "fabric" },
    { key: "fabrics",          label: "fabric" },

    // Colour — legacy single + family + multi-select array
    { key: "color",            label: "colour" },
    { key: "colour_family",    label: "colour" },
    { key: "colour_families",  label: "colour" },

    // Occasion — legacy single + multi-select array
    { key: "occasion",         label: "occasion" },
    { key: "occasions",        label: "occasion" },

    // Work type — legacy single + multi-select array (e.g. "Zardosi",
    // "Mirror work") — frequently mentioned in user queries.
    { key: "work_type",        label: "work" },
    { key: "work_types",       label: "work" },

    // Free-form curation tags (lower-kebab slugs — e.g. "diwali",
    // "navratri", "wedding"). Critical: this is where "Diwali" lives on
    // most boutique items.
    { key: "tags",             label: "tag" },

    // Storytelling / details — admins frequently surface festival /
    // occasion language inside these fields rather than tagging.
    { key: "boutique_note",    label: "details" },
    { key: "notes",            label: "details" },
    { key: "care_instructions", label: "care" },
    { key: "highlights",       label: "highlight" },
    { key: "included_items",   label: "includes" },
];

/**
 * WP3 (jewellery vertical) — per-department dynamic attributes
 * (`department_attributes`, e.g. metal_type / stone_type / plating /
 * dimensions / closure / set_type) live in a nested dict rather than a flat
 * field, so they're flattened separately from SEARCH_FIELDS. This lets a
 * shopper searching "kundan" or "oxidised" reach the jewellery that carries
 * those attributes even when they aren't in the name/tags.
 */
export function deptAttrBlob(item) {
    const attrs = item?.department_attributes;
    if (!attrs || typeof attrs !== "object") return "";
    const parts = [];
    for (const v of Object.values(attrs)) {
        if (Array.isArray(v)) parts.push(...v.filter(Boolean).map(String));
        else if (typeof v === "string" && v) parts.push(v);
        else if (typeof v === "number") parts.push(String(v));
    }
    return parts.join(" ").toLowerCase();
}

/**
 * WP3 — minimal, safe synonym map so common Hinglish jewellery queries land
 * on the right pieces. Keys are the term a shopper types; values are the
 * catalogue words they should ALSO match (category / tags / name). Kept
 * deliberately small — jewellery-only, no clothing terms — so it can never
 * hijack an existing clothing search.
 */
export const SEARCH_SYNONYMS = {
    jhumka: ["earrings"],
    jhumki: ["earrings"],
    payal: ["anklets"],
    kada: ["bangles"],
    haar: ["necklace"],
    mala: ["necklace"],
    tikka: ["maang tikka"],
    set: ["necklace set"],
};

/**
 * Expand a single lowercase token into itself + any synonym targets.
 * Returns a flat lowercase list; the token matches if ANY entry is found in
 * the item's haystack.
 */
export function expandToken(token) {
    const extra = SEARCH_SYNONYMS[token];
    return extra ? [token, ...extra.map((s) => s.toLowerCase())] : [token];
}

/**
 * Flatten a single item into a lower-cased "haystack" string covering
 * every SEARCH_FIELDS entry. Arrays are joined with spaces; non-string
 * scalars (numbers, booleans) are coerced to "".
 */
export function buildSearchBlob(item) {
    const base = SEARCH_FIELDS
        .map(({ key }) => {
            const raw = item?.[key];
            if (Array.isArray(raw)) return raw.filter(Boolean).join(" ");
            return typeof raw === "string" ? raw : "";
        })
        .join(" ")
        .toLowerCase();
    // WP3 — append nested department_attributes values (metal, stones, …).
    const attrs = deptAttrBlob(item);
    return attrs ? `${base} ${attrs}` : base;
}

/**
 * Returns true if every whitespace-separated token in `query` is found
 * somewhere in the item's searchable blob. Empty / short queries return
 * `true` so callers can short-circuit before invoking us.
 */
export function itemMatchesQuery(item, query) {
    const ql = String(query || "").trim().toLowerCase();
    if (!ql) return true;
    const tokens = ql.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return true;
    const blob = buildSearchBlob(item);
    // WP3 — each token matches if the token OR any of its synonym targets is
    // found in the haystack.
    return tokens.every((t) => expandToken(t).some((v) => blob.includes(v)));
}

/**
 * Per-field inspection used by the SmartSearch dropdown to render
 * "matched on tag · diwali" chips. Returns:
 *   { match: bool, fields: [{label, value}] }
 *
 * `match` follows the SAME semantics as `itemMatchesQuery` — every
 * token must hit at least one field — so the two helpers can never
 * disagree on which items qualify.
 */
export function inspectItem(item, query) {
    const ql = String(query || "").trim().toLowerCase();
    if (!ql) return { match: true, fields: [] };
    const tokens = ql.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return { match: true, fields: [] };

    const tokensCovered = new Set();
    const hits = [];
    const seen = new Set();  // de-dupe by `${label}::${value}`

    // WP3 — treat the nested department_attributes as one extra searchable
    // "attributes" field so the dropdown surfaces matches on metal / stones.
    const fields = [...SEARCH_FIELDS, { key: "__dept_attrs__", label: "attributes" }];

    for (const { key, label } of fields) {
        const raw = key === "__dept_attrs__" ? deptAttrBlob(item) : item?.[key];
        const isArray = Array.isArray(raw);
        const flat = isArray
            ? raw.filter(Boolean).join(" ")
            : (typeof raw === "string" ? raw : "");
        if (!flat) continue;
        const lower = flat.toLowerCase();
        // A token matches this field when the token OR any synonym target is
        // present. Coverage is tracked against the ORIGINAL token so the
        // "every token covered" contract with itemMatchesQuery holds.
        const matched = tokens.filter((t) => expandToken(t).some((v) => lower.includes(v)));
        if (matched.length === 0) continue;
        matched.forEach((t) => tokensCovered.add(t));

        // For array fields, surface the SPECIFIC element that matched
        // (e.g. tags=["diwali","festive"] + query="diwali" → "diwali").
        let display = flat;
        if (isArray) {
            const exact = raw.find((v) =>
                matched.some((t) => expandToken(t).some((s) => String(v || "").toLowerCase().includes(s)))
            );
            if (exact) display = String(exact);
        }
        const dedupeKey = `${label}::${String(display).toLowerCase()}`;
        if (!seen.has(dedupeKey)) {
            seen.add(dedupeKey);
            hits.push({ label, value: display });
        }
    }
    return { match: tokensCovered.size === tokens.length, fields: hits };
}
